import { useTranslation } from '../../../i18n';
import { formatMoney, PLATFORM_CURRENCY } from '../../../utils/money';
import React, { useMemo, useState } from 'react';
import {
    Alert, Button, Card, Checkbox, Descriptions, Form, Input, InputNumber, Modal, Popconfirm, Select, Space, Spin, Tag, Tooltip, Typography, theme,
} from 'antd';
import { DeleteOutlined, DisconnectOutlined, PlusOutlined, ReloadOutlined } from '@ant-design/icons';
import StatusTag from '../../StatusTag';
import CompactTable from './CompactTable';
import RdnsCard from './RdnsCard';
import { ok, runAction, toArray } from './utils';

const { Text } = Typography;

const prefixFromNetmask = (mask) => {
    if (!mask) return 24;
    const bits = String(mask).split('.').map(Number);
    if (bits.length !== 4 || bits.some(Number.isNaN)) return mask;
    return bits.reduce((s, b) => s + ((b >>> 0).toString(2).match(/1/g) || []).length, 0);
};

const NetworkTab = ({ id, vm, api, adapter, network, loading, reloadNetwork, isMobile, disabled }) => {
    const { t } = useTranslation();
    const { token } = theme.useToken();
    const isAdmin = adapter.isAdmin;
    const mono = { fontFamily: token.fontFamilyCode };

    const [submitting, setSubmitting] = useState(false);
    // modals
    const [ipOpen, setIpOpen] = useState(false);
    const [availableIps, setAvailableIps] = useState([]);
    const [eipOpen, setEipOpen] = useState(false);
    const [freeEips, setFreeEips] = useState([]);
    const [vpcOpen, setVpcOpen] = useState(false);
    const [pfOpen, setPfOpen] = useState(false);
    const [siteOpen, setSiteOpen] = useState(false);

    const [ipForm] = Form.useForm();
    const [eipForm] = Form.useForm();
    const [vpcForm] = Form.useForm();
    const [pfForm] = Form.useForm();
    const [siteForm] = Form.useForm();
    const vpcId = Form.useWatch('vpc_id', vpcForm);
    const siteDomain = Form.useWatch('domain', siteForm);

    const net = network || {};
    const publicIps = net.public_ips || [];
    const eips = net.eips || [];
    const vpcPorts = net.vpc_ports || [];
    const forwards = net.port_forwards || [];
    const attachable = net.attachable_vpcs || [];
    const hasNat = publicIps.some((p) => p.nat);
    // NAT / VPC forwarding: node address, tenant port range, quotas {used, max}, websites
    const fw = net.forwarding || null;
    const sites = fw?.site_list || [];
    const full = (q) => !!q && q.used >= q.max;
    const usage = (q) => (q ? <Text type="secondary" style={{ fontSize: 12 }}>{q.used}/{q.max}</Text> : null);

    // Merge detail IPs (gateway/mask/dns) with network flags (nat/primary).
    const ipRows = useMemo(() => {
        const detail = new Map((vm.ips || []).map((ip) => [String(ip.id), ip]));
        const rows = publicIps.map((p) => ({ ...(detail.get(String(p.id)) || {}), ...p }));
        (vm.ips || []).forEach((ip) => {
            if (!rows.find((r) => String(r.id) === String(ip.id))) {
                rows.push({ ...ip, primary: ip.is_main, version: ip.type === 'v6' ? 6 : 4 });
            }
        });
        return rows;
    }, [publicIps, vm.ips]);

    const subnets = (attachable.find((v) => v.id === vpcId)?.subnets) || [];
    // Cross-AZ / cross-region extension of the selected VPC: billed, confirmed below.
    const cross = attachable.find((v) => v.id === vpcId)?.cross || null;
    const crossLabel = (c) => (c?.class === 'cross_region' ? t('Cross-region') : t('Cross-AZ'));

    const after = (res, close) => {
        if (res) {
            close?.();
            reloadNetwork();
        }
    };

    // ---------- admin: bind/unbind pool IP ----------
    const openBindIp = async () => {
        ipForm.resetFields();
        setIpOpen(true);
        const res = await api.getAvailableIps(id);
        setAvailableIps(ok(res) ? toArray(res.data, 'ips') : []);
    };
    const bindIp = async (v) => {
        setSubmitting(true);
        const res = await runAction(() => api.bindIp(id, { ip_id: v.ip_id, init: v.init ? 'yes' : 'no' }), t('IP attached'), t('Failed to attach IP'));
        setSubmitting(false);
        after(res, () => setIpOpen(false));
    };
    const unbindIp = async (ipId) => after(await runAction(() => api.unbindIp(id, ipId), t('IP detached'), t('Failed to detach IP')));

    // ---------- EIP ----------
    const openBindEip = async () => {
        eipForm.resetFields();
        setEipOpen(true);
        const res = await api.getEipList();
        const list = ok(res) ? toArray(res.data, 'eips') : [];
        setFreeEips(list.filter((e) => e.status === 'available' && (!e.node_id || !vm.node?.id || e.node_id === vm.node.id)));
    };
    const bindEip = async (v) => {
        setSubmitting(true);
        const res = await runAction(() => api.bindEip({ eip_id: v.eip_id, vm_id: Number(id) }), t('EIP attached'), t('Failed to attach EIP'));
        setSubmitting(false);
        after(res, () => setEipOpen(false));
    };
    const unbindEip = async (eipId) => after(await runAction(() => api.unbindEip(eipId), t('EIP detached'), t('Failed to detach EIP')));

    // ---------- VPC ----------
    const attachVpc = async (v) => {
        setSubmitting(true);
        const res = await runAction(() => api.attachVpc(id, v.subnet_id, !!v.restart, !!cross), t('Network interface added'), t('Failed to add network interface'));
        setSubmitting(false);
        after(res, () => setVpcOpen(false));
    };
    const detachVpc = (port) => {
        let restart = false;
        Modal.confirm({
            title: t('Remove network interface {{ip}}?', { ip: port.ip }),
            content: <Checkbox onChange={(e) => { restart = e.target.checked; }}>{t('Restart now to apply')}</Checkbox>,
            okText: t('Remove'),
            okButtonProps: { danger: true },
            cancelText: t('Cancel'),
            onOk: async () => after(await runAction(() => api.detachVpc(id, port.id, restart), t('Network interface removed'), t('Failed to remove network interface'))),
        });
    };

    // ---------- port forward ----------
    const addForward = async (v) => {
        setSubmitting(true);
        const res = await runAction(() => api.createPortForward({
            vm_id: Number(id),
            protocol: v.protocol,
            external_port: v.external_port || '',
            internal_port: v.internal_port,
            description: v.description || '',
        }), t('Port forwarding added'), t('Failed to add'));
        setSubmitting(false);
        after(res, () => setPfOpen(false));
    };
    const deleteForward = async (pfId) => after(await runAction(() => api.deletePortForward(pfId), t('Deleted'), t('Deletion failed')));

    // ---------- websites (NAT) ----------
    const addSite = async (v) => {
        setSubmitting(true);
        const res = await runAction(() => api.createSiteForward({ vm_id: Number(id), domain: v.domain }), t('Added'), t('Failed to add'));
        setSubmitting(false);
        after(res, () => setSiteOpen(false));
    };
    const deleteSite = async (sid) => after(await runAction(() => api.deleteSiteForward(sid), t('Deleted'), t('Deletion failed')));

    if (loading && !network) return <div style={{ textAlign: 'center', padding: 48 }}><Spin /></div>;

    const ipDetail = (ip) => (
        <Descriptions size="small" column={isMobile ? 1 : 3}>
            <Descriptions.Item label="MAC"><Text copyable style={mono}>{ip.mac || '-'}</Text></Descriptions.Item>
            <Descriptions.Item label={t('Gateway')}><Text copyable={!!ip.gateway} style={mono}>{ip.gateway || '-'}</Text></Descriptions.Item>
            <Descriptions.Item label={ip.type === 'v6' ? t('Prefix') : t('Mask')}>
                {ip.type === 'v6'
                    ? (ip.prefix ? <Text copyable style={mono}>{ip.prefix}</Text> : (ip.prefix_len ? `/${ip.prefix_len}` : '-'))
                    : (ip.netmask || '-')}
            </Descriptions.Item>
            <Descriptions.Item label="DNS">{[ip.dns1, ip.dns2].filter(Boolean).join(' / ') || '-'}</Descriptions.Item>
            {isAdmin && <Descriptions.Item label={t('Network bridge')}>{ip.bridge || '-'}</Descriptions.Item>}
            {isAdmin && <Descriptions.Item label={t('IP pool')}>{ip.pool_name || '-'}</Descriptions.Item>}
            {!ip.primary && !ip.is_main && ip.address && (
                <Descriptions.Item label={t('Configuration command')} span={isMobile ? 1 : 3}>
                    <Text copyable code style={{ fontSize: 12 }}>
                        {ip.type === 'v6'
                            ? `ip addr add ${ip.address}/${ip.prefix_len || 128} dev eth0`
                            : `ip addr add ${ip.address}/${prefixFromNetmask(ip.netmask)} dev eth0`}
                    </Text>
                </Descriptions.Item>
            )}
        </Descriptions>
    );

    const ipColumns = [
        {
            title: t('Address'), key: 'address', render: (_, r) => (
                <Space size={0} direction="vertical">
                    <Text copyable={{ text: r.address }} style={mono}>{r.address}</Text>
                    {r.type === 'v6' && r.prefix && <Text type="secondary" copyable={{ text: r.prefix }} style={{ ...mono, fontSize: 12 }}>{r.prefix}</Text>}
                </Space>
            ),
        },
        {
            title: t('Type'), key: 'type', render: (_, r) => (
                <Space size={4}>
                    {(r.primary || r.is_main) && <Tag color="blue">{t('Primary')}</Tag>}
                    {r.nat && <Tag color="orange">NAT</Tag>}
                    <Tag>{r.version === 6 || r.type === 'v6' ? 'IPv6' : 'IPv4'}</Tag>
                </Space>
            ),
        },
        ...(isAdmin ? [{
            title: '', key: 'actions', align: 'right', render: (_, r) => ((r.primary || r.is_main) ? null : (
                <Popconfirm title={t('Unbind this IP?')} okButtonProps={{ danger: true }} onConfirm={() => unbindIp(r.id)} disabled={disabled}>
                    <Button size="small" type="text" danger icon={<DisconnectOutlined />} disabled={disabled}>{t('Unbind')}</Button>
                </Popconfirm>
            )),
        }] : []),
    ];

    const eipColumns = [
        { title: t('Address'), key: 'address', render: (_, r) => <Text copyable={{ text: r.address }} style={mono}>{r.address}</Text> },
        { title: t('Version'), key: 'version', render: (_, r) => (String(r.version).includes('6') ? 'IPv6' : 'IPv4') },
        { title: t('Status'), key: 'status', render: (_, r) => <StatusTag status={r.status} domain="eip" /> },
        {
            title: '', key: 'actions', align: 'right', render: (_, r) => (
                <Popconfirm title={t('Unbind this EIP?')} okButtonProps={{ danger: true }} onConfirm={() => unbindEip(r.id)} disabled={disabled}>
                    <Button size="small" type="text" danger icon={<DisconnectOutlined />} disabled={disabled}>{t('Unbind')}</Button>
                </Popconfirm>
            ),
        },
    ];

    const vpcColumns = [
        { title: 'VPC', key: 'vpc', render: (_, r) => r.vpc || `#${r.vpc_id}` },
        { title: t('Subnet'), key: 'subnet', render: (_, r) => <Text style={mono}>{r.subnet || '-'}</Text> },
        { title: t('Private IP'), key: 'ip', render: (_, r) => <Text copyable={{ text: r.ip }} style={mono}>{r.ip}</Text> },
        { title: 'MAC', key: 'mac', render: (_, r) => <Text type="secondary" style={{ ...mono, fontSize: 12 }}>{r.mac || '-'}</Text> },
        {
            title: '', key: 'actions', align: 'right', render: (_, r) => (
                <Button size="small" type="text" danger icon={<DeleteOutlined />} disabled={disabled} onClick={() => detachVpc(r)}>{t('Remove')}</Button>
            ),
        },
    ];

    const pfColumns = [
        { title: t('Protocol'), key: 'protocol', render: (_, r) => String(r.protocol || '').toUpperCase() },
        {
            title: t('Address'), key: 'external_port', render: (_, r) => {
                const addr = fw?.host ? `${fw.host}:${r.external_port}` : String(r.external_port);
                return <Text copyable={{ text: addr }} style={mono}>{addr}</Text>;
            },
        },
        { title: t('Internal port'), key: 'internal_port', dataIndex: 'internal_port' },
        { title: t('Notes'), key: 'description', render: (_, r) => r.description || '-' },
        {
            title: t('Status'), key: 'status', render: (_, r) => (
                <Space size={4}>
                    <StatusTag status={r.status} />
                    {r.sg_open === false && <Tooltip title={t('A security group blocks this port')}><Tag color="warning">{t('Blocked')}</Tag></Tooltip>}
                </Space>
            ),
        },
        {
            title: '', key: 'actions', align: 'right', render: (_, r) => (
                <Popconfirm title={t('Delete this forwarding rule?')} okButtonProps={{ danger: true }} onConfirm={() => deleteForward(r.id)} disabled={disabled}>
                    <Button size="small" type="text" danger icon={<DeleteOutlined />} disabled={disabled}>{t('Delete')}</Button>
                </Popconfirm>
            ),
        },
    ];

    const siteColumns = [
        { title: t('Domains'), key: 'domain', render: (_, r) => <Text copyable style={mono}>{r.domain}</Text> },
        { title: t('Status'), key: 'status', render: (_, r) => <StatusTag status={r.status} /> },
        {
            title: '', key: 'actions', align: 'right', render: (_, r) => (
                <Popconfirm title={t('Delete this website?')} okButtonProps={{ danger: true }} onConfirm={() => deleteSite(r.id)} disabled={disabled}>
                    <Button size="small" type="text" danger icon={<DeleteOutlined />} disabled={disabled}>{t('Delete')}</Button>
                </Popconfirm>
            ),
        },
    ];

    return (
        <Space direction="vertical" size={16} style={{ width: '100%' }}>
            {!network && !loading && (
                <Alert type="error" showIcon message={t('Failed to load network information')} action={<Button size="small" onClick={reloadNetwork}>{t('Retry')}</Button>} />
            )}

            <Card
                size="small"
                title={t('Public IP')}
                extra={(
                    <Space size={4}>
                        <Button size="small" type="text" icon={<ReloadOutlined />} onClick={reloadNetwork} loading={loading} />
                        {isAdmin && <Button size="small" icon={<PlusOutlined />} onClick={openBindIp} disabled={disabled}>{t('Add IP')}</Button>}
                    </Space>
                )}
            >
                <CompactTable
                    isMobile={isMobile}
                    columns={ipColumns}
                    dataSource={ipRows}
                    rowKey={(r) => r.id || r.address}
                    emptyText={t('No public IP yet')}
                    expandable={{ expandedRowRender: ipDetail, rowExpandable: (r) => !!(r.mac || r.gateway) }}
                />
                {(vm.ipv6_blocks || []).length > 0 && (
                    <Descriptions size="small" column={1} style={{ marginTop: 12 }}>
                        {(vm.ipv6_blocks || []).map((b) => (
                            <Descriptions.Item key={b.prefix} label={t('Routed IPv6 prefix')}>
                                <Space size={8} wrap>
                                    <Text copyable style={mono}>{b.prefix}</Text>
                                    {b.via && <Text type="secondary" style={{ ...mono, fontSize: 12 }}>via {b.via}</Text>}
                                </Space>
                            </Descriptions.Item>
                        ))}
                    </Descriptions>
                )}
            </Card>

            <RdnsCard id={id} api={api} isMobile={isMobile} disabled={disabled} isAdmin={isAdmin} />

            <Card
                size="small"
                title={t('Elastic IPs')}
                extra={!isAdmin && <Button size="small" icon={<PlusOutlined />} onClick={openBindEip} disabled={disabled}>{t('Attach EIP')}</Button>}
            >
                <CompactTable isMobile={isMobile} columns={eipColumns} dataSource={eips} emptyText={t('No elastic IP bound')} />
            </Card>

            <Card
                size="small"
                title={t('VPC network interface')}
                extra={(
                    <Button
                        size="small"
                        icon={<PlusOutlined />}
                        disabled={disabled || attachable.length === 0}
                        title={attachable.length === 0 ? t('No VPC available') : undefined}
                        onClick={() => { vpcForm.resetFields(); setVpcOpen(true); }}
                    >
                        {t('Add network interface')}
                    </Button>
                )}
            >
                <CompactTable isMobile={isMobile} columns={vpcColumns} dataSource={vpcPorts} emptyText={t('Not in a VPC')} />
            </Card>

            {(fw || hasNat || forwards.length > 0) && (
                <Card
                    size="small"
                    title={t('Port forwarding')}
                    extra={(
                        <Space size={8}>
                            {usage(fw?.forwards)}
                            <Button
                                size="small"
                                icon={<PlusOutlined />}
                                disabled={disabled || full(fw?.forwards)}
                                onClick={() => { pfForm.resetFields(); setPfOpen(true); }}
                            >
                                {t('Add forwarding')}
                            </Button>
                        </Space>
                    )}
                >
                    <CompactTable isMobile={isMobile} columns={pfColumns} dataSource={forwards} emptyText={t('No port forwarding yet')} />
                </Card>
            )}

            {fw?.sites && (
                <Card
                    size="small"
                    title={<Tooltip title={t('HTTP/HTTPS forwarded by domain to the instance\'s 80/443')}>{t('Websites')}</Tooltip>}
                    extra={(
                        <Space size={8}>
                            {usage(fw.sites)}
                            <Button
                                size="small"
                                icon={<PlusOutlined />}
                                disabled={disabled || full(fw.sites)}
                                onClick={() => { siteForm.resetFields(); setSiteOpen(true); }}
                            >
                                {t('Add website')}
                            </Button>
                        </Space>
                    )}
                >
                    <CompactTable isMobile={isMobile} columns={siteColumns} dataSource={sites} emptyText={t('No websites yet')} />
                </Card>
            )}

            {/* admin: bind pool IP */}
            <Modal title={t('Add IP')} open={ipOpen} onCancel={() => setIpOpen(false)} onOk={() => ipForm.submit()} confirmLoading={submitting} destroyOnClose>
                <Form form={ipForm} layout="vertical" onFinish={bindIp}>
                    <Form.Item name="ip_id" label="IP" rules={[{ required: true, message: t('Select an IP') }]}>
                        <Select
                            showSearch
                            optionFilterProp="label"
                            placeholder={t('Select an IP')}
                            options={availableIps.map((ip) => ({
                                value: ip.id,
                                label: `${ip.address}${ip.pool_name ? ` · ${ip.pool_name}` : ''}${ip.type ? ` (${String(ip.type).toUpperCase()})` : ''}`,
                            }))}
                        />
                    </Form.Item>
                    <Form.Item name="init" valuePropName="checked" tooltip={t('Additional IPs must be configured manually in the OS')}>
                        <Checkbox>{t('Take effect immediately')}</Checkbox>
                    </Form.Item>
                </Form>
            </Modal>

            {/* user: bind EIP */}
            <Modal title={t('Attach elastic IP')} open={eipOpen} onCancel={() => setEipOpen(false)} onOk={() => eipForm.submit()} confirmLoading={submitting} destroyOnClose>
                <Form form={eipForm} layout="vertical" onFinish={bindEip}>
                    <Form.Item name="eip_id" label={t('Elastic IPs')} rules={[{ required: true, message: t('Select') }]} extra={freeEips.length === 0 ? t('No elastic IPs available to bind') : null}>
                        <Select
                            placeholder={t('Select an Elastic IP')}
                            options={freeEips.map((e) => ({ value: e.id, label: `${e.public_ip}${e.ip_version ? ` (${e.ip_version})` : ''}` }))}
                        />
                    </Form.Item>
                </Form>
            </Modal>

            {/* VPC attach */}
            <Modal title={t('Add VPC network interface')} open={vpcOpen} onCancel={() => setVpcOpen(false)} onOk={() => vpcForm.submit()} confirmLoading={submitting} destroyOnClose>
                <Form form={vpcForm} layout="vertical" onFinish={attachVpc} initialValues={{ restart: false }}>
                    <Form.Item name="vpc_id" label="VPC" rules={[{ required: true, message: t('Select a VPC') }]}>
                        <Select
                            placeholder={t('Select a VPC')}
                            onChange={() => vpcForm.setFieldsValue({ subnet_id: undefined })}
                            options={attachable.map((v) => ({ value: v.id, label: `${v.name}${v.cidr ? ` · ${v.cidr}` : ''}${v.cross ? ` · ${crossLabel(v.cross)}` : ''}` }))}
                        />
                    </Form.Item>
                    <Form.Item name="subnet_id" label={t('Subnet')} rules={[{ required: true, message: t('Select a subnet') }]}>
                        <Select
                            placeholder={t('Select a subnet')}
                            disabled={!vpcId}
                            options={subnets.map((s) => ({ value: s.id, label: `${s.name}${s.cidr ? ` · ${s.cidr}` : ''}` }))}
                        />
                    </Form.Item>
                    {cross && (
                        <Form.Item
                            name="cross_ok"
                            valuePropName="checked"
                            rules={[{ validator: (_, v) => (v ? Promise.resolve() : Promise.reject(new Error(t('Please confirm the price')))) }]}
                        >
                            <Checkbox>
                                {t('{{kind}} traffic {{price}}/GB', { kind: crossLabel(cross), price: formatMoney(cross.price_per_gb, cross.currency || PLATFORM_CURRENCY) })}
                            </Checkbox>
                        </Form.Item>
                    )}
                    <Form.Item name="restart" valuePropName="checked" style={{ marginBottom: 0 }}>
                        <Checkbox>{t('Restart now to apply')}</Checkbox>
                    </Form.Item>
                </Form>
            </Modal>

            {/* Port forward */}
            <Modal title={t('Add port forwarding')} open={pfOpen} onCancel={() => setPfOpen(false)} onOk={() => pfForm.submit()} confirmLoading={submitting} destroyOnClose>
                <Form form={pfForm} layout="vertical" onFinish={addForward} initialValues={{ protocol: 'tcp' }}>
                    <Form.Item name="protocol" label={t('Protocol')}>
                        <Select options={[{ value: 'tcp', label: 'TCP' }, { value: 'udp', label: 'UDP' }]} />
                    </Form.Item>
                    <div style={{ display: 'flex', gap: 8 }}>
                        <Form.Item name="external_port" label={t('External port')} style={{ flex: 1 }}>
                            <InputNumber min={fw?.port_min || 1} max={fw?.port_max || 65535} placeholder={t('Leave empty to auto-assign')} style={{ width: '100%' }} />
                        </Form.Item>
                        <Form.Item name="internal_port" label={t('Internal port')} rules={[{ required: true, message: t('Required') }]} style={{ flex: 1 }}>
                            <InputNumber min={1} max={65535} style={{ width: '100%' }} />
                        </Form.Item>
                    </div>
                    <Form.Item name="description" label={t('Notes')} style={{ marginBottom: 0 }}>
                        <Input maxLength={64} />
                    </Form.Item>
                </Form>
            </Modal>

            {/* Website (NAT) */}
            <Modal title={t('Add website')} open={siteOpen} onCancel={() => setSiteOpen(false)} onOk={() => siteForm.submit()} confirmLoading={submitting} destroyOnClose>
                <Form form={siteForm} layout="vertical" onFinish={addSite}>
                    <Form.Item name="domain" label={t('Domains')} rules={[{ required: true, message: t('Required') }]} style={{ marginBottom: isAdmin ? 0 : 12 }}>
                        <Input placeholder="www.example.com" />
                    </Form.Item>
                    {!isAdmin && fw?.site_verify && (
                        <Text type="secondary" style={{ fontSize: 12 }}>
                            {t('First add a TXT record to prove ownership')}: <Text style={mono}>{`_yeap-verify.${(siteDomain || '').trim() || 'example.com'}`}</Text> = <Text copyable style={{ ...mono, fontSize: 12 }}>{fw.site_verify.value}</Text>
                        </Text>
                    )}
                </Form>
            </Modal>
        </Space>
    );
};

export default NetworkTab;
